import type { Value } from '@udecode/plate-common';
import { Plate, usePlateEditorState } from '@udecode/plate-core';
import type { FC, ReactNode } from 'react';
import { useImperativeHandle, useMemo } from 'react';
import AIHelperEmailWriter from '../AIHelper/AIHelperEmailWriter';
import { EditorRef, TextEditorType } from './TextEditor.types';
import plugins from './TextEditorPlugins';

const EditorRefSpy: FC<{ editorRef?: EditorRef }> = ({ editorRef }) => {
  const editor = usePlateEditorState();
  useImperativeHandle(editorRef, () => editor);
  return null;
};

type RenderWithEditorType = {
  render: (editor: TextEditorType) => ReactNode;
};
const RenderWithEditor: FC<RenderWithEditorType> = ({ render }) => {
  const editor = usePlateEditorState();
  return <>{render(editor)}</>;
};

export type TextEditorProps = {
  renderFooter: (editor: TextEditorType) => ReactNode;
  onChange?: (value: Value) => void;
  initialValue?: Value;
  editorRef?: EditorRef;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  replaceDefaultPlugins?: boolean;
  plugins?: any[];
  aiHelper?: boolean;
  variablesData?: Record<string, any>;
};

const TextEditor: FC<TextEditorProps> = ({
  renderFooter,
  onChange,
  initialValue,
  editorRef,
  className,
  onFocus,
  onBlur,
  plugins: propsPlugins,
  replaceDefaultPlugins,
  aiHelper,
  variablesData,
}) => {
  return (
    <div className="flex flex-col relative flex-1">
      <Plate
        normalizeInitialValue
        editableProps={{ className, onFocus, onBlur }}
        initialValue={initialValue}
        plugins={useMemo(() => {
          if (replaceDefaultPlugins) return propsPlugins;
          return plugins.concat(propsPlugins || []);
        }, [replaceDefaultPlugins, propsPlugins])}
        onChange={onChange}
      >
        <EditorRefSpy editorRef={editorRef} />
        <RenderWithEditor render={renderFooter} />
      </Plate>

      {aiHelper && (
        <AIHelperEmailWriter
          className="absolute right-3 top-3"
          editorRef={editorRef}
          variablesData={variablesData || {}}
        />
      )}
    </div>
  );
};

export default TextEditor;

export type { EditorRef, TextEditorType };
